import type { GetSubmissionAttemptDetailResponse } from '@/services/api/agentflowService';

export interface SubmissionAttemptPresentationState {
  blockingFieldsCount: number;
  confirmationFieldsCount: number;
  nonBlockingFieldsCount: number;
  hasDestinationBlocker: boolean;
  hasSignatureBlocker: boolean;
  manualInputCount: number;
  reviewBlocked: boolean;
}

function getBlockerItems(detail: GetSubmissionAttemptDetailResponse) {
  return Array.isArray(detail.blocker_items) ? detail.blocker_items : [];
}

function getBlockingReasons(detail: GetSubmissionAttemptDetailResponse) {
  return Array.isArray(detail.blockers) ? detail.blockers : [];
}

export function getSubmissionAttemptPresentationState(
  detail: GetSubmissionAttemptDetailResponse,
): SubmissionAttemptPresentationState {
  const blockingFieldsCount = detail.fields.items.filter(
    (field) => field.disposition === 'blocking',
  ).length;
  const confirmationFieldsCount = detail.fields.items.filter(
    (field) => field.disposition === 'requires_confirmation',
  ).length;
  const nonBlockingFieldsCount = detail.fields.items.filter(
    (field) => field.disposition === 'non_blocking',
  ).length;
  const hasDestinationBlocker =
    detail.destination != null && detail.destination.state !== 'resolved';
  const hasSignatureBlocker =
    detail.signature != null && !detail.signature.resolved;
  const manualInputCount =
    (hasDestinationBlocker ? 1 : 0) +
    (hasSignatureBlocker ? 1 : 0) +
    blockingFieldsCount +
    confirmationFieldsCount;
  const reviewBlocked =
    (detail.status === 'blocked' ||
      detail.status === 'needs_rebuild' ||
      detail.status === 'needs_re_evaluation') &&
    manualInputCount === 0;

  return {
    blockingFieldsCount,
    confirmationFieldsCount,
    nonBlockingFieldsCount,
    hasDestinationBlocker,
    hasSignatureBlocker,
    manualInputCount,
    reviewBlocked,
  };
}

export function getSubmissionAttemptSummary(
  detail: GetSubmissionAttemptDetailResponse,
): {
  title: string | null;
  description: string | null;
} {
  const state = getSubmissionAttemptPresentationState(detail);

  if (state.manualInputCount > 0) {
    return {
      title: `${state.manualInputCount} item${state.manualInputCount !== 1 ? 's' : ''} need${state.manualInputCount === 1 ? 's' : ''} your input`,
      description:
        'Resolve the highlighted destination, signature, or field items before rebuilding the package.',
    };
  }

  if (state.reviewBlocked) {
    const blockerItems = getBlockerItems(detail);
    const blockingReasons = getBlockingReasons(detail);
    const blockerSummary =
      blockerItems.find((item) => item.summary.trim().length > 0)?.summary ??
      blockingReasons.find((blocker) => blocker.trim().length > 0) ??
      detail.reviewer_summary;
    const nextAction =
      blockerItems.find(
        (item) => item.recommended_action != null && item.recommended_action.trim().length > 0,
      )?.recommended_action ??
      detail.recommended_next_action;
    const fallbackTitle = 'Blocked after review';
    const title = blockerSummary ?? fallbackTitle;
    let description = 'Check the preview before rebuilding.';
    if (nextAction != null && nextAction !== title) {
      description = nextAction;
    } else if (
      detail.reviewer_summary != null &&
      detail.reviewer_summary !== title
    ) {
      description = detail.reviewer_summary;
    }

    return {
      title,
      description,
    };
  }

  return {
    title: null,
    description: null,
  };
}

export function getSubmissionAttemptRebuildHelperText(
  detail: GetSubmissionAttemptDetailResponse,
): string {
  const state = getSubmissionAttemptPresentationState(detail);

  if (state.hasDestinationBlocker) {
    return 'Choose a destination above to rebuild the package.';
  }
  if (state.hasSignatureBlocker) {
    return 'Resolve signature above to rebuild the package.';
  }
  if (state.blockingFieldsCount > 0 || state.confirmationFieldsCount > 0) {
    return 'Resolve the items above to rebuild the package.';
  }
  if (state.reviewBlocked) {
    const blockerItems = getBlockerItems(detail);
    return (
      blockerItems.find(
        (item) => item.recommended_action != null && item.recommended_action.trim().length > 0,
      )?.recommended_action ??
      detail.recommended_next_action ??
      'Review the draft PDF for the blocking issue, then rebuild the package.'
    );
  }
  return '';
}
